import React, { useEffect, useState, useCallback } from 'react';
import { useAxios } from '../../Helpers/AxiosHelper';
import { useTranslation } from 'react-i18next';
import { Calendar, Card, Column, ColumnFilterElementTemplateOptions, DataTable, DataTableFilterMeta, DDCountries, DDStatus, FilterMatchMode, ProgressBar, SCIcon, Tag } from '../../Common/Imports';
import { ApiResponse, Reservations_EXT } from '../../Models/Classes';
import OperativeContacts from './Components/Contacts';
import ReservationDetail from '../Reservations/Reservation_Detail';
import { useToast } from '../../Helpers/ToastHelper';
import OperativePaxes from './Components/Paxes';
import OperativeComunications from './Components/Comunications';
import { ExcelExport } from '../../Common/Utility/ExcelExport';

export const OperativeListUnreadEmails: React.FC = () => {
    const { t } = useTranslation();
    const [TableData, SetTableData] = useState<Reservations_EXT[]>([]);
    const [TableLoader, SetTableLoader] = useState<boolean>(false);
    const [SelectedData, setSelectedData] = useState<Reservations_EXT | undefined>();
    const [IDReservation, SetIDReservation] = useState<number | undefined>();
    const [type, setType] = useState<number | undefined>();
    const AxiosHelper = useAxios();
    const { showToast } = useToast();
    const [TableFilters, setTableFilters] = useState<DataTableFilterMeta>({
        idReservation: { value: null, matchMode: FilterMatchMode.CONTAINS },
        sendState: { value: null, matchMode: FilterMatchMode.CONTAINS },
        idReservationOperationalStatus: { value: null, matchMode: FilterMatchMode.EQUALS },
        idReservationCustomerStatus: { value: null, matchMode: FilterMatchMode.CONTAINS },
        providerReservationCode: { value: null, matchMode: FilterMatchMode.CONTAINS },
        customerReservationCode: { value: null, matchMode: FilterMatchMode.CONTAINS },
        propertyName: { value: null, matchMode: FilterMatchMode.CONTAINS },
        country: { value: null, matchMode: FilterMatchMode.EQUALS },
        refPax: { value: null, matchMode: FilterMatchMode.CONTAINS },
        checkInDate: { value: null, matchMode: FilterMatchMode.DATE_IS },
        checkOutDate: { value: null, matchMode: FilterMatchMode.DATE_IS },
        cancellationDate: { value: null, matchMode: FilterMatchMode.DATE_IS }
    });

    const GetData = useCallback(async () => {
        SetTableLoader(true);
        try {
            const resp = await AxiosHelper.get<ApiResponse<Reservations_EXT[]>>(`Operative/GetListWithUnreadMail`);
            if (resp.data.code === 0) {
                const processedData = resp.data.data.map(item => ({
                    ...item,
                    checkInDate: new Date(item.checkInDate),
                    checkOutDate: new Date(item.checkOutDate),
                    bookingDate: new Date(item.bookingDate)
                }));
                SetTableData(processedData);
            }
        } catch (error) {
            console.error('Error fetching data:', error);
        } finally {
            SetTableLoader(false);
        }
    }, [AxiosHelper]);

    useEffect(() => {
        GetData();
    }, [GetData]);

    const DoubleBodyTemplate = (Name1: string, Name2: string) => {
        return (
            <>
                {Name1}
                <br />
                {Name2}
            </>
        )
    }

    const UpdateRowData = async (Data: Reservations_EXT) => {
        try {
            await AxiosHelper.post(`Reservation/UpdateReservation`, Data);
            showToast('success', 'Success', t("Common.Toasts.Update.Success"));
            GetData();
        } catch (error: unknown) {
            console.log(error);
            showToast('error', 'Error', t("Common.Toasts.Update.Error"));
        }
    }

    const UpdateOperationalStatus = (RowData: Reservations_EXT, StatusId: number) => {
        RowData.idReservationOperationalStatus = StatusId;
        UpdateRowData(RowData);
    }

    const OperativeStatusTemplate = (RowData: Reservations_EXT) => {
        return <DDStatus IDType={2} value={RowData.idReservationOperationalStatus} onChange={e => {
            if (e) UpdateOperationalStatus(RowData, e);
        }
        } />;
    };

    const dateFilterTemplate = (options: ColumnFilterElementTemplateOptions) => {
        return <Calendar
            value={options.value}
            onChange={(e) => options.filterCallback(e.value, options.index)}
            dateFormat="dd/mm/yy" placeholder="dd/mm/yyyy" mask="99/99/9999" />;
    };

    const CountryFilterTemplate = (options: ColumnFilterElementTemplateOptions) => {
        return <DDCountries value={options.value} onChange={(e) => options.filterCallback(e, options.index)} />;
    };

    const StatusFilterTemplate = (options: ColumnFilterElementTemplateOptions) => {
        return (
            <DDStatus
                IDType={2}
                value={options.value}
                onChange={(e) => {
                    if (e !== undefined && e !== null) {
                        options.filterCallback(e, options.index); // Pass the value correctly to the filter callback
                    }
                }}
            />
        );
    };

    const HeaderTemplate = (
        <div className="flex justify-content-between align-items-center pt-2 pe-2 ps-2">
            <div className="flex align-items-center">
                <SCIcon Name="Refresh" Pointer OnClick={() => GetData()} />
            </div>
            <div className="flex align-items-center">
                <SCIcon Name="Export" Pointer OnClick={() => ExcelExport(TableData, TableFilters, "EXPORT_Reservations")} />
            </div>
        </div>
    );
    const rowClass = (data: Reservations_EXT) => {
        return data.internalStatus.cssClasses;
    };

    const CustomerStatusTemplate = (RowData: Reservations_EXT) => {
        return <Tag className={RowData.customerStatus.cssClasses}>{RowData.customerStatus.value}</Tag>;
    };

    return (
        <>
            {
                SelectedData && type === 0 && <OperativeContacts
                    IDReservation={SelectedData.idReservation}
                    IDProperty={SelectedData.idProperty}
                    ShowPropertyContacts
                    ShowReservationContacts
                    OnHide={() => {
                        setSelectedData(undefined);
                        GetData();
                    }} />
            }
            {
                IDReservation &&
                <ReservationDetail
                    IDReservation={IDReservation}
                    OnHide={() => {
                        SetIDReservation(undefined);
                    }}
                />
            }
            {
                SelectedData && type === 1 &&
                <OperativePaxes
                    IDReservation={SelectedData.idReservation} />
            }
            {
                SelectedData && type === 2 &&
                <OperativeComunications
                    IDReservation={SelectedData.idReservation}
                    OnHide={() => {
                        setSelectedData(undefined);
                        setType(undefined);
                        GetData();
                    }} />
            }
            <div className="row">
                <div className="col-md-12">
                    <Card header={HeaderTemplate}>
                        {
                            TableLoader ?
                                <ProgressBar mode="indeterminate" style={{ height: '6px' }}></ProgressBar>
                                :
                                <DataTable
                                    value={TableData}
                                    size="small"
                                    stripedRows
                                    paginator
                                    rows={10}
                                    onFilter={(e) => setTableFilters(e.filters)}
                                    filters={TableFilters}
                                    rowsPerPageOptions={[10, 20, 50]}
                                    currentPageReportTemplate="Showing {first} to {last} of {totalRecords} records"
                                    paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
                                    rowClassName={rowClass}
                                    tableStyle={{ minWidth: '50rem' }}>
                                    <Column
                                        filter
                                        sortable
                                        header={t("Operative.Table.ResId")}
                                        field="idReservation"
                                        bodyClassName="table-cell-nowrap" />
                                    <Column
                                        body={(e: Reservations_EXT) => (
                                            <>
                                                <SCIcon
                                                    Pointer
                                                    Name="Info"
                                                    Tooltip={t("Operative.Table.OpenDetail")}
                                                    OnClick={() => SetIDReservation(e.idReservation)}
                                                />
                                                <SCIcon
                                                    Pointer
                                                    Name="Forum"
                                                    OnClick={() => {
                                                        setSelectedData(e);
                                                        setType(2);
                                                    }}
                                                />
                                            </>
                                        )}
                                        bodyClassName="table-cell-nowrap"
                                    />
                                    <Column
                                        filter
                                        sortable
                                        header={t("Operative.Table.OperativeStatus")}
                                        field="idReservationOperationalStatus"
                                        filterElement={StatusFilterTemplate}
                                        body={(e: Reservations_EXT) => OperativeStatusTemplate(e)}
                                        bodyClassName="table-cell-nowrap"
                                    />
                                    <Column
                                        filter
                                        sortable
                                        field="customerStatus.value"
                                        header={t("Operative.Table.ResCustomerStatus")}
                                        filterField="idReservationCustomerStatus"
                                        bodyClassName="table-cell-nowrap"
                                        body={(e: Reservations_EXT) => CustomerStatusTemplate(e)}
                                    />
                                    <Column
                                        filter
                                        sortable
                                        field="stakeholder.name"
                                        header={t("Operative.Table.Stakeholder")}
                                        filterField="stakeholder.name"
                                        bodyClassName="table-cell-nowrap"
                                    />
                                    <Column
                                        filter
                                        sortable
                                        field="brand.name"
                                        header={t("Operative.Table.Brand")}
                                        filterField="brand.name"
                                        bodyClassName="table-cell-nowrap"
                                    />
                                    <Column
                                        filter
                                        sortable
                                        header={t("Operative.Table.ProviderReservationCode")}
                                        field="providerReservationCode"
                                        bodyClassName="table-cell-nowrap" body={(e: Reservations_EXT) => { return <a className="underline text-primary" style={{ cursor: "pointer" }} onClick={() => SetIDReservation(e.idReservation)}>{e.providerReservationCode}</a> }}
                                    />
                                    <Column
                                        filter
                                        sortable
                                        header={t("Operative.Table.CustomerReservationCode")}
                                        field="customerReservationCode"
                                        bodyClassName="table-cell-nowrap" />
                                    <Column
                                        filter
                                        sortable
                                        field="propertyName"
                                        header={t("Operative.Table.HotelName")}
                                        body={(e: Reservations_EXT) => DoubleBodyTemplate(e.propertyName, e.property?.name)}
                                        bodyClassName="table-cell-nowrap" />
                                    <Column
                                        field="country"
                                        sortable
                                        filterElement={CountryFilterTemplate}
                                        header={t("Operative.Table.Country")}
                                        body={(e: Reservations_EXT) => DoubleBodyTemplate(e.propertyCountry, e.property?.country)}
                                        bodyClassName="table-cell-nowrap" />
                                    <Column
                                        filter
                                        field="refPax"
                                        sortable
                                        header={t("Operative.Table.RefPax")}
                                        body={(e: Reservations_EXT) => {
                                            return <a className="underline text-primary" style={{ cursor: "pointer" }}
                                                onClick={() => {
                                                    setSelectedData(e);
                                                    setType(1);
                                                }}>{e.refPax}</a>
                                        }}
                                        bodyClassName="table-cell-nowrap" />
                                    <Column
                                        filter
                                        dataType="date"
                                        sortable
                                        header={t("Operative.Table.CheckIn")}
                                        body={(e: Reservations_EXT) => e.checkInDate.toLocaleDateString()}
                                        filterElement={dateFilterTemplate}
                                        filterField="checkInDate"
                                        bodyClassName="table-cell-nowrap" />
                                    <Column
                                        filter
                                        dataType="date"
                                        sortable
                                        header={t("Operative.Table.CheckOut")}
                                        body={(e: Reservations_EXT) => e.checkOutDate.toLocaleDateString()}
                                        filterElement={dateFilterTemplate}
                                        filterField="checkOutDate"
                                        bodyClassName="table-cell-nowrap" />
                                    <Column
                                        filter
                                        sortable
                                        dataType="date"
                                        header={t("Operative.Table.CancellationDate")}
                                        body={(e: Reservations_EXT) => e.cancellationDate?.toLocaleDateString()}
                                        filterElement={dateFilterTemplate}
                                        filterField="cancellationDate"
                                        sortField="cancellationDate"
                                        bodyClassName="table-cell-nowrap"
                                    />
                                    <Column
                                        body={(e: Reservations_EXT) => {
                                            return <SCIcon
                                                Pointer
                                                Name='Contacts'
                                                OnClick={() => {
                                                    setSelectedData(e)
                                                    setType(0);
                                                }} />
                                        }}
                                        bodyClassName="table-cell-nowrap" />
                                </DataTable>
                        }
                    </Card>
                </div>
            </div>
        </>
    );
};

export default OperativeListUnreadEmails;
